// Every call the app makes to the database. Row-level security decides what each person sees.
import { db } from "./lib/supabase";
import { plan } from "./modules";
import type { Branch, Department, Member, Membership, Module, Preset, Workspace } from "./types";

function check<T>(res: { data: T | null; error: { message: string } | null }): T {
  if (res.error) throw new Error(res.error.message);
  return res.data as T;
}

type RoleRow = { key: string; name_en: string; name_ar: string };
const one = <T,>(v: T | T[]): T => (Array.isArray(v) ? v[0] : v);

export async function loadMemberships(userId: string): Promise<Membership[]> {
  const rows = check(
    await db()
      .from("company_members")
      .select("id, company_id, employment_id, display_name, active, companies(id, name, name_ar, default_language, country_code), roles(key, name_en, name_ar)")
      .eq("user_id", userId)
      .eq("active", true),
  ) as unknown as Array<{
    id: string;
    company_id: string;
    employment_id: string | null;
    display_name: string | null;
    companies: Membership["company"] | Membership["company"][];
    roles: RoleRow | RoleRow[];
  }>;
  return rows.map((r) => ({
    id: r.id,
    company_id: r.company_id,
    employment_id: r.employment_id,
    display_name: r.display_name,
    company: one(r.companies),
    role: one(r.roles),
  }));
}

export async function loadCatalog(): Promise<{ modules: Module[]; presets: Preset[] }> {
  const [modules, presets] = await Promise.all([
    db().from("modules").select("*").order("sort_order"),
    db().from("presets").select("*"),
  ]);
  const order = ["basic_startup", "growing_company", "full_hr"];
  return {
    modules: check(modules) as Module[],
    presets: (check(presets) as Preset[]).sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key)),
  };
}

export async function loadWorkspace(membership: Membership, catalog: Module[]): Promise<Workspace> {
  const [mods, perms, team] = await Promise.all([
    db().from("company_modules").select("module_key, enabled").eq("company_id", membership.company_id),
    db().rpc("my_permissions", { p_company: membership.company_id }),
    db().rpc("my_team"),
  ]);
  const enabled = new Set(
    (check(mods) as Array<{ module_key: string; enabled: boolean }>).filter((m) => m.enabled).map((m) => m.module_key),
  );
  const permissions = new Set(check(perms) as string[]);
  const teamSize = team.error ? 0 : ((team.data as unknown[]) ?? []).length;
  return { membership, catalog, enabled, permissions, teamSize };
}

export async function createCompany(input: {
  name: string;
  nameAr: string;
  language: "en" | "ar";
  preset: string;
  modules: Set<string>;
  catalog: Module[];
}): Promise<string> {
  const companyId = check(
    await db().rpc("create_company", {
      p_name: input.name.trim(),
      p_country: "EG",
      p_preset: input.preset,
      p_name_ar: input.nameAr.trim() || null,
      p_default_language: input.language,
    }),
  ) as string;
  // the database switched on the preset's modules; adjust to what was ticked in the wizard
  const current = new Set(
    (check(
      await db().from("company_modules").select("module_key, enabled").eq("company_id", companyId),
    ) as Array<{ module_key: string; enabled: boolean }>)
      .filter((m) => m.enabled)
      .map((m) => m.module_key),
  );
  await applyModules(companyId, current, input.modules, input.catalog);
  return companyId;
}

export async function applyModules(companyId: string, current: Set<string>, target: Set<string>, catalog: Module[]) {
  const { switchOn, switchOff } = plan(current, target, catalog);
  for (const k of switchOn) check(await db().rpc("enable_module", { p_company: companyId, p_module: k }));
  for (const k of switchOff) {
    check(await db().from("company_modules").update({ enabled: false }).eq("company_id", companyId).eq("module_key", k).select());
  }
}

export async function loadOrganisation(companyId: string): Promise<{ departments: Department[]; branches: Branch[] }> {
  const [d, b] = await Promise.all([
    db().from("departments").select("id, name, name_ar, parent_id, active").eq("company_id", companyId).order("name"),
    db().from("branches").select("id, name, name_ar, is_head_office").eq("company_id", companyId).order("name"),
  ]);
  return { departments: check(d) as Department[], branches: check(b) as Branch[] };
}

export async function addDepartment(companyId: string, name: string, nameAr: string, parentId: string | null) {
  check(
    await db()
      .from("departments")
      .insert({ company_id: companyId, name: name.trim(), name_ar: nameAr.trim() || null, parent_id: parentId })
      .select(),
  );
}

export async function loadMembers(companyId: string): Promise<Member[]> {
  const rows = check(
    await db()
      .from("company_members")
      .select("id, user_id, employment_id, display_name, active, roles(key, name_en, name_ar)")
      .eq("company_id", companyId)
      .order("display_name"),
  ) as unknown as Array<Omit<Member, "role"> & { roles: RoleRow | RoleRow[] }>;
  return rows.map(({ roles, ...r }) => ({ ...r, role: one(roles) }));
}

// ---------------------------------------------------------------- Core HR (M3)
import type { Assignment, ContractType, DirectoryRow, DocRow, ImportResult, Person } from "./types";

export async function loadDirectory(companyId: string): Promise<DirectoryRow[]> {
  return check(
    await db().from("employee_directory").select("*").eq("company_id", companyId).order("full_name"),
  ) as DirectoryRow[];
}

export async function loadContractTypes(): Promise<ContractType[]> {
  return check(await db().from("contract_types").select("code, name_en, name_ar, needs_end_date").order("sort_order")) as ContractType[];
}

export async function addEmployee(companyId: string, fields: Record<string, string>): Promise<string> {
  const clean = Object.fromEntries(Object.entries(fields).filter(([, v]) => v.trim() !== ""));
  return check(await db().rpc("add_employee", { p_company: companyId, p: clean })) as string;
}

export const importEmployeesV2 = async (companyId: string, rows: Record<string, string>[], dryRun: boolean) =>
  check(await db().rpc("import_employees_v2", { p_company: companyId, p_rows: rows, p_dry_run: dryRun })) as ImportResult[];
export async function importEmployees(companyId: string, rows: Record<string, string>[]): Promise<ImportResult[]> {
  return check(await db().rpc("import_employees", { p_company: companyId, p_rows: rows })) as ImportResult[];
}

export async function loadEmployee(employmentId: string): Promise<{ row: DirectoryRow; person: Person; history: Assignment[] }> {
  const row = check(await db().from("employee_directory").select("*").eq("employment_id", employmentId).single()) as DirectoryRow;
  const [person, history] = await Promise.all([
    db().from("persons").select("id, first_name, last_name, first_name_ar, last_name_ar, gender, date_of_birth, nationality, national_id, mobile, personal_email").eq("id", row.person_id).single(),
    db().from("assignments").select("id, start_date, end_date, job_title, job_title_ar, grade, fte, contract_type_code, change_reason, department_id").eq("employment_id", employmentId).order("start_date", { ascending: false }),
  ]);
  return { row, person: check(person) as Person, history: check(history) as Assignment[] };
}

export async function loadDocuments(companyId: string): Promise<DocRow[]> {
  return check(await db().rpc("hiring_documents_status", { p_company: companyId })) as DocRow[];
}

export async function setDocument(
  companyId: string,
  employmentId: string,
  documentTypeId: string,
  status: "received" | "waived" | null,
  receivedOn?: string,
  expiryDate?: string,
) {
  if (status === null) {
    check(await db().from("employee_documents").delete().eq("employment_id", employmentId).eq("document_type_id", documentTypeId).select());
    return;
  }
  check(
    await db()
      .from("employee_documents")
      .upsert(
        {
          company_id: companyId,
          employment_id: employmentId,
          document_type_id: documentTypeId,
          status,
          received_on: status === "received" ? receivedOn || new Date().toISOString().slice(0, 10) : null,
          expiry_date: status === "received" && expiryDate ? expiryDate : null,
        },
        { onConflict: "employment_id,document_type_id" },
      )
      .select(),
  );
}

// ---------------------------------------------------------------- Sample data
export async function sampleDataLoaded(companyId: string): Promise<boolean> {
  return check(await db().rpc("sample_data_loaded", { p_company: companyId })) as boolean;
}

export async function loadSampleData(companyId: string): Promise<number> {
  const n = check(await db().rpc("load_sample_data", { p_company: companyId })) as number;
  // attendance, leave, letters and payroll for the sample people (best effort)
  await db().rpc("load_sample_activity", { p_company: companyId });
  await db().rpc("load_sample_payroll", { p_company: companyId });
  await db().rpc("load_sample_talent", { p_company: companyId });
  await db().rpc("load_sample_growth", { p_company: companyId });
  await db().rpc("load_sample_culture", { p_company: companyId });
  return n;
}

export async function removeSampleData(companyId: string): Promise<number> {
  return check(await db().rpc("remove_sample_data", { p_company: companyId })) as number;
}

// ---------------------------------------------------------------- Employee actions (M3c)
export interface LeavingReason { code: string; name_en: string; name_ar: string; category: string }

export async function loadLeavingReasons(): Promise<LeavingReason[]> {
  return check(await db().from("leaving_reasons").select("code, name_en, name_ar, category").order("sort_order")) as LeavingReason[];
}

export async function updatePerson(personId: string, fields: Record<string, string>) {
  const values = Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, v.trim() === "" ? null : v.trim()]));
  check(await db().from("persons").update(values).eq("id", personId).select());
}

export async function updateEmployment(employmentId: string, fields: Record<string, string>) {
  const values = Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, v.trim() === "" ? null : v.trim()]));
  check(await db().from("employments").update(values).eq("id", employmentId).select());
}

export async function changeJob(employmentId: string, start: string, fields: Record<string, string>) {
  const clean = Object.fromEntries(Object.entries(fields).filter(([, v]) => v.trim() !== ""));
  check(await db().rpc("change_job", { p_employment: employmentId, p_start: start, p: clean }));
}

export async function setManager(employmentId: string, managerId: string | null, start: string) {
  check(await db().rpc("set_manager", { p_employment: employmentId, p_manager: managerId, p_start: start }));
}

export async function terminateEmployment(employmentId: string, lastDay: string, reason: string, regrettable: boolean, rehire: boolean) {
  check(await db().rpc("terminate_employment", {
    p_employment: employmentId, p_last_day: lastDay, p_reason: reason, p_regrettable: regrettable, p_rehire_eligible: rehire,
  }));
}

// ---------------------------------------------------------------- Settings (M3c)
export interface CustomFieldDef {
  id: string;
  entity: "person" | "employment";
  key: string;
  label_en: string;
  label_ar: string | null;
  field_type: "text" | "number" | "date" | "boolean" | "select";
  options: string[] | null;
  required: boolean;
  active: boolean;
  sort_order: number;
}

export interface DocumentType {
  id: string;
  key: string;
  name_en: string;
  name_ar: string | null;
  required_for_hire: boolean;
  applies_to: "all" | "male" | "female" | "egyptian" | "non_egyptian";
  has_expiry: boolean;
  active: boolean;
  sort_order: number;
}

export async function loadCustomFields(companyId: string): Promise<CustomFieldDef[]> {
  return check(await db().from("custom_field_defs").select("*").eq("company_id", companyId).order("sort_order").order("label_en")) as CustomFieldDef[];
}

export async function saveCustomField(companyId: string, def: Partial<CustomFieldDef> & { id?: string }) {
  if (def.id) {
    const { id, ...rest } = def;
    check(await db().from("custom_field_defs").update(rest).eq("id", id).select());
  } else {
    check(await db().from("custom_field_defs").insert({ ...def, company_id: companyId }).select());
  }
}

export async function loadDocumentTypes(companyId: string): Promise<DocumentType[]> {
  return check(await db().from("document_types").select("*").eq("company_id", companyId).order("sort_order")) as DocumentType[];
}

export async function saveDocumentType(companyId: string, doc: Partial<DocumentType> & { id?: string }) {
  if (doc.id) {
    const { id, ...rest } = doc;
    check(await db().from("document_types").update(rest).eq("id", id).select());
  } else {
    check(await db().from("document_types").insert({ ...doc, company_id: companyId }).select());
  }
}

export async function linkMember(memberId: string, employmentId: string | null) {
  check(await db().rpc("link_member_employment", { p_member: memberId, p_employment: employmentId }));
}

export async function loadCustomValues(personId: string, employmentId: string) {
  const [p, e] = await Promise.all([
    db().from("persons").select("custom").eq("id", personId).single(),
    db().from("employments").select("custom").eq("id", employmentId).single(),
  ]);
  return {
    person: ((check(p) as { custom: Record<string, unknown> }).custom ?? {}),
    employment: ((check(e) as { custom: Record<string, unknown> }).custom ?? {}),
  };
}

export async function saveCustomValues(table: "persons" | "employments", id: string, custom: Record<string, unknown>) {
  check(await db().from(table).update({ custom }).eq("id", id).select());
}

/** Turns a label into a field key: "Shift pattern" → "shift_pattern". */
export function toKey(label: string): string {
  const k = label.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40);
  return /^[a-z]/.test(k) ? k : `field_${Date.now().toString(36)}`;
}

// ---------------------------------------------------------------- Leave (M4)
export interface LeaveType {
  id: string; key: string; name_en: string; name_ar: string | null; paid: boolean; entitlement: string;
  days_per_year: number | null; days_per_event: number | null; applies_to: "all" | "male" | "female";
  requires_document: boolean; counts: string; active: boolean; sort_order: number;
}
export type LeaveStatus = "pending" | "approved" | "rejected" | "cancelled";
export interface LeaveRequest {
  id: string; employment_id: string; leave_type_id: string; start_date: string; end_date: string; days: number;
  status: LeaveStatus; note: string | null; decision_note: string | null; created_at: string;
}
export interface Balance {
  leave_type_id: string; key: string; name_en: string; name_ar: string | null;
  entitlement: number; carried_over: number; adjusted: number; taken: number; pending: number; remaining: number;
}
export interface Holiday { holiday_date: string; name_en: string; name_ar: string | null; tentative?: boolean }

export async function loadLeaveTypes(companyId: string): Promise<LeaveType[]> {
  return check(await db().from("leave_types").select("*").eq("company_id", companyId).order("sort_order")) as LeaveType[];
}

export async function loadLeaveRequests(companyId: string, employmentId?: string): Promise<LeaveRequest[]> {
  let q = db().from("leave_requests")
    .select("id, employment_id, leave_type_id, start_date, end_date, days, status, note, decision_note, created_at")
    .eq("company_id", companyId).order("start_date", { ascending: false }).limit(500);
  if (employmentId) q = q.eq("employment_id", employmentId);
  return check(await q) as LeaveRequest[];
}

export async function requestLeave(employmentId: string, typeId: string, start: string, end: string, note: string, approve: boolean) {
  return check(await db().rpc("request_leave", {
    p_employment: employmentId, p_type: typeId, p_start: start, p_end: end, p_note: note, p_approve: approve,
  })) as string;
}

export async function decideLeave(requestId: string, approve: boolean, note?: string) {
  check(await db().rpc("decide_leave", { p_request: requestId, p_approve: approve, p_note: note ?? null }));
}

export async function cancelLeave(requestId: string) {
  check(await db().rpc("cancel_leave", { p_request: requestId }));
}

export async function loadBalances(employmentId: string, year: number): Promise<Balance[]> {
  return check(await db().rpc("my_leave_balances", { p_employment: employmentId, p_year: year })) as Balance[];
}

export async function addAdjustment(companyId: string, employmentId: string, typeId: string, year: number, days: number, reason: string) {
  check(await db().from("leave_adjustments").insert({
    company_id: companyId, employment_id: employmentId, leave_type_id: typeId, year, days, reason,
  }).select());
}

export async function loadHolidays(companyId: string, country: string, year: number): Promise<{ publicDays: Holiday[]; companyDays: Holiday[] }> {
  const from = `${year}-01-01`, to = `${year}-12-31`;
  const [p, c] = await Promise.all([
    db().from("public_holidays").select("holiday_date, name_en, name_ar, tentative").eq("country_code", country)
      .gte("holiday_date", from).lte("holiday_date", to).order("holiday_date"),
    db().from("company_holidays").select("holiday_date, name_en, name_ar").eq("company_id", companyId)
      .gte("holiday_date", from).lte("holiday_date", to).order("holiday_date"),
  ]);
  return { publicDays: check(p) as Holiday[], companyDays: check(c) as Holiday[] };
}

export async function addCompanyHoliday(companyId: string, date: string, name: string) {
  check(await db().from("company_holidays").insert({ company_id: companyId, holiday_date: date, name_en: name.trim() }).select());
}

export async function removeCompanyHoliday(companyId: string, date: string) {
  check(await db().from("company_holidays").delete().eq("company_id", companyId).eq("holiday_date", date).select());
}

// ---------------------------------------------------------------- Attendance (M4c)
export type AttStatus = "present" | "late" | "on_leave" | "absent" | "day_off" | "upcoming";
export interface AttDay {
  employment_id: string; employee_number: string; full_name: string; full_name_ar: string | null; department_name: string | null;
  status: AttStatus; check_in: string | null; check_out: string | null; minutes_late: number;
}
export interface AttSummary {
  employment_id: string; employee_number: string; full_name: string; full_name_ar: string | null; department_name: string | null;
  working_days: number; present_days: number; late_days: number; late_minutes: number; leave_days: number; absent_days: number; missing_checkout: number;
}

export async function loadAttendanceDay(companyId: string, day: string): Promise<AttDay[]> {
  return check(await db().rpc("attendance_day", { p_company: companyId, p_day: day })) as AttDay[];
}

export async function loadAttendanceSummary(companyId: string, from: string, to: string): Promise<AttSummary[]> {
  return check(await db().rpc("attendance_summary", { p_company: companyId, p_from: from, p_to: to })) as AttSummary[];
}

export async function saveAttendance(companyId: string, employmentId: string, day: string, checkIn: string, checkOut: string) {
  if (!checkIn && !checkOut) {
    check(await db().from("attendance_records").delete().eq("employment_id", employmentId).eq("work_date", day).select());
    return;
  }
  check(await db().from("attendance_records").upsert({
    company_id: companyId, employment_id: employmentId, work_date: day,
    check_in: checkIn || null, check_out: checkOut || null, source: "manual",
  }, { onConflict: "employment_id,work_date" }).select());
}

export async function importAttendance(companyId: string, rows: Record<string, string>[], source: "import" | "device"): Promise<ImportResult[]> {
  return check(await db().rpc("import_attendance", { p_company: companyId, p_rows: rows, p_source: source })) as ImportResult[];
}

// ---------------------------------------------------------------- Workflows (M5)
export interface Approval {
  request_id: string; company_id: string; employment_id: string; employee_name: string; employee_name_ar: string | null;
  leave_type_en: string; leave_type_ar: string | null; start_date: string; end_date: string; days: number; note: string | null;
  stage: "manager" | "hr"; created_at: string;
}
export interface TeamMember {
  employment_id: string; employee_number: string; full_name: string; full_name_ar: string | null; job_title: string | null;
  job_title_ar: string | null; department_name: string | null; department_name_ar: string | null; status: string;
  today: "present" | "late" | "on_leave" | "absent" | "day_off"; check_in: string | null;
}
export interface Alert {
  kind: "contract_end" | "probation_end" | "document_expiring" | "document_expired" | "documents_overdue" | "birthday";
  employment_id: string; full_name: string; full_name_ar: string | null; due_date: string; days_left: number; detail: string | null;
}
export interface Invite { id: string; email: string; role_key: string; code: string; created_at: string; expires_at: string }
export interface MyInvite { code: string; company_id: string; company_name: string; company_name_ar: string | null; role_en: string; role_ar: string }

export const loadApprovals = async () => check(await db().rpc("my_approvals")) as Approval[];
export const loadTeam = async () => check(await db().rpc("my_team")) as TeamMember[];
export const loadAlerts = async (companyId: string) => check(await db().rpc("company_alerts", { p_company: companyId })) as Alert[];
export const loadMemberNames = async (companyId: string) =>
  check(await db().rpc("member_names", { p_company: companyId })) as { user_id: string; display_name: string }[];

export async function delegateApprovals(toUser: string, start: string, end: string) {
  check(await db().rpc("delegate_approvals", { p_to_user: toUser, p_start: start, p_end: end }));
}

export async function loadInvites(companyId: string): Promise<Invite[]> {
  return check(await db().from("company_invites").select("id, email, role_key, code, created_at, expires_at")
    .eq("company_id", companyId).is("accepted_at", null).is("revoked_at", null).order("created_at", { ascending: false })) as Invite[];
}

export async function createInvite(companyId: string, email: string, role: string, employmentId: string | null) {
  return check(await db().rpc("create_invite", { p_company: companyId, p_email: email, p_role: role, p_employment: employmentId })) as string;
}

export async function revokeInvite(inviteId: string) {
  check(await db().rpc("revoke_invite", { p_invite: inviteId }));
}

export const loadMyInvites = async () => check(await db().rpc("my_invites")) as MyInvite[];
export const acceptInvite = async (code: string) => check(await db().rpc("accept_invite", { p_code: code })) as string;

export async function setMemberRole(memberId: string, roleKey: string) {
  check(await db().rpc("set_member_role", { p_member: memberId, p_role_key: roleKey }));
}
export async function setMemberActive(memberId: string, active: boolean) {
  check(await db().rpc("set_member_active", { p_member: memberId, p_active: active }));
}
export async function loadRoles(companyId: string): Promise<{ key: string; name_en: string; name_ar: string }[]> {
  return check(await db().from("roles").select("key, name_en, name_ar").eq("company_id", companyId).order("name_en")) as { key: string; name_en: string; name_ar: string }[];
}

export async function saveSetting(companyId: string, key: string, value: unknown) {
  check(await db().from("company_settings").upsert({ company_id: companyId, setting_key: key, value }, { onConflict: "company_id,setting_key" }).select());
}

export async function getSetting(companyId: string, key: string): Promise<unknown> {
  return check(await db().rpc("get_setting", { p_company: companyId, p_key: key }));
}

// ---------------------------------------------------------------- Letters and policies (M6)
export interface LetterTemplate {
  id: string; key: string; name_en: string; name_ar: string | null; body_en: string; body_ar: string;
  self_service: boolean; active: boolean; sort_order: number;
}
export interface LetterRequest { id: string; employment_id: string; template_id: string; purpose: string | null; status: string; created_at: string }
export interface IssuedLetter { id: string; employment_id: string; reference_no: string; title: string; body_en: string; body_ar: string; issued_at: string }
export interface MyPolicy {
  policy_id: string; key: string; version: number; title_en: string; title_ar: string | null; body_en: string; body_ar: string | null;
  requires_ack: boolean; published_at: string; acknowledged_at: string | null;
}
export interface PolicyStatus {
  policy_id: string; key: string; version: number; title_en: string; title_ar: string | null; published_at: string;
  requires_ack: boolean; employees: number; acknowledged: number;
}

export async function loadLetterTemplates(companyId: string): Promise<LetterTemplate[]> {
  return check(await db().from("letter_templates").select("*").eq("company_id", companyId).order("sort_order")) as LetterTemplate[];
}
export async function saveLetterTemplate(id: string, patch: Partial<LetterTemplate>) {
  check(await db().from("letter_templates").update(patch).eq("id", id).select());
}
export const letterData = async (employmentId: string) =>
  check(await db().rpc("letter_data", { p_employment: employmentId })) as Record<string, string>;
export async function issueLetter(employmentId: string, templateId: string, title: string, en: string, ar: string, requestId?: string) {
  return check(await db().rpc("issue_letter", {
    p_employment: employmentId, p_template: templateId, p_title: title, p_body_en: en, p_body_ar: ar, p_request: requestId ?? null,
  })) as string;
}
export async function loadLetterRequests(companyId: string, employmentId?: string): Promise<LetterRequest[]> {
  let q = db().from("letter_requests").select("id, employment_id, template_id, purpose, status, created_at").eq("company_id", companyId).order("created_at", { ascending: false });
  if (employmentId) q = q.eq("employment_id", employmentId);
  return check(await q) as LetterRequest[];
}
export const requestLetter = async (templateId: string, purpose: string) =>
  check(await db().rpc("request_letter", { p_template: templateId, p_purpose: purpose })) as string;
export const declineLetterRequest = async (id: string) => { check(await db().rpc("decline_letter_request", { p_request: id })); };
export async function loadIssuedLetters(companyId: string, employmentId?: string): Promise<IssuedLetter[]> {
  let q = db().from("letters_issued").select("id, employment_id, reference_no, title, body_en, body_ar, issued_at").eq("company_id", companyId).order("issued_at", { ascending: false }).limit(300);
  if (employmentId) q = q.eq("employment_id", employmentId);
  return check(await q) as IssuedLetter[];
}
export const loadMyPolicies = async (companyId: string) => check(await db().rpc("my_policies", { p_company: companyId })) as MyPolicy[];
export const loadPolicyStatus = async (companyId: string) => check(await db().rpc("policy_ack_status", { p_company: companyId })) as PolicyStatus[];
export const acknowledgePolicy = async (id: string) => { check(await db().rpc("acknowledge_policy", { p_policy: id })); };
export async function publishPolicy(companyId: string, key: string, titleEn: string, bodyEn: string, titleAr: string, bodyAr: string, requiresAck: boolean) {
  return check(await db().rpc("publish_policy", {
    p_company: companyId, p_key: key, p_title_en: titleEn, p_body_en: bodyEn, p_title_ar: titleAr, p_body_ar: bodyAr, p_requires_ack: requiresAck,
  })) as string;
}

// ---------------------------------------------------------------- Dashboard (M7)
export interface TrendPoint { month: string; headcount: number; hires: number; leavers_voluntary: number; leavers_other: number; in_period: boolean }
export interface DashboardData {
  as_of: string; period: { from: string; to: string; days: number };
  headcount: number; headcount_start: number; sample_headcount: number;
  hires: number; leavers: number; voluntary_leavers: number;
  turnover: number | null; total_turnover: number | null; turnover_annualised: number | null; early_leavers: number;
  avg_tenure_years: number | null; avg_age: number | null; in_probation: number; leave_days: number;
  attendance_rate: number | null; late_rate: number | null;
  starting_soon: number; on_leave_today: number; pending_leave: number; documents_overdue: number;
  trend: TrendPoint[];
  by_department: { name: string; name_ar: string; count: number }[];
  by_gender: Record<string, number>; by_contract: Record<string, number>;
  by_tenure: Record<string, number>; by_age: Record<string, number>; by_nationality: Record<string, number>;
}
export const loadDashboard = async (companyId: string, from: string, to: string) =>
  check(await db().rpc("dashboard", { p_company: companyId, p_from: from, p_to: to })) as DashboardData;

// ---------------------------------------------------------------- Approvals, self-service and alerts (M5 completion)
export const resetSampleData = async (companyId: string) => check(await db().rpc("reset_sample_data", { p_company: companyId })) as number;

export interface Decision {
  request_id: string; employment_id: string; employee_name: string; employee_name_ar: string | null;
  leave_type_en: string; leave_type_ar: string; start_date: string; end_date: string; days: number;
  status: "approved" | "rejected" | "pending"; step: "manager" | "hr"; decided_at: string; note: string | null;
}
export const loadDecisions = async () => check(await db().rpc("my_decisions", { p_days: 90 })) as Decision[];

export interface Delegation { id: string; direction: "given" | "received"; other_name: string; start_date: string; end_date: string; active: boolean }
export const loadDelegations = async () => check(await db().rpc("my_delegations")) as Delegation[];
export async function cancelDelegation(id: string) {
  check(await db().from("approval_delegations").delete().eq("id", id).select());
}

export const PROFILE_FIELDS = ["mobile", "personal_email", "address", "emergency_contact_name", "emergency_contact_phone", "marital_status", "education_level"] as const;
export type ProfileField = (typeof PROFILE_FIELDS)[number];
export type ProfileDetails = Record<ProfileField, string | null>;
export interface ProfileChange {
  id: string; employment_id: string; changes: Partial<ProfileDetails>; previous: Partial<ProfileDetails>; note: string | null;
  status: "pending" | "approved" | "rejected" | "cancelled"; created_at: string; decided_at: string | null; decision_note: string | null;
}
export async function loadProfileChanges(companyId: string, employmentId?: string): Promise<ProfileChange[]> {
  let q = db().from("profile_change_requests")
    .select("id, employment_id, changes, previous, note, status, created_at, decided_at, decision_note")
    .eq("company_id", companyId).order("created_at", { ascending: false }).limit(200);
  if (employmentId) q = q.eq("employment_id", employmentId);
  return check(await q) as ProfileChange[];
}
export async function loadMyDetails(employmentId: string): Promise<ProfileDetails> {
  const e = check(await db().from("employments").select("person_id").eq("id", employmentId).single()) as { person_id: string };
  return check(await db().from("persons").select(PROFILE_FIELDS.join(", ")).eq("id", e.person_id).single()) as unknown as ProfileDetails;
}
export const requestProfileChange = async (companyId: string, changes: Partial<ProfileDetails>, note: string) =>
  check(await db().rpc("request_profile_change", { p_company: companyId, p_changes: changes, p_note: note || null })) as string;
export const decideProfileChange = async (id: string, approve: boolean, note?: string) =>
  check(await db().rpc("decide_profile_change", { p_request: id, p_approve: approve, p_note: note || null })) as string;
export const cancelProfileChange = async (id: string) => { check(await db().rpc("cancel_profile_change", { p_request: id })); };
export async function loadEducationLevels(): Promise<{ code: string; name_en: string; name_ar: string }[]> {
  return check(await db().from("education_levels").select("code, name_en, name_ar").order("sort_order")) as { code: string; name_en: string; name_ar: string }[];
}

export interface Reminder {
  kind: "policy_ack" | "document_expiring" | "document_expired" | "leave_approved" | "leave_rejected" | "letter_ready"
      | "letter_declined" | "profile_approved" | "profile_rejected" | "probation_end";
  ref_id: string; title_en: string | null; title_ar: string | null; due_date: string | null; detail: string | null;
}
export const loadReminders = async (companyId: string) => check(await db().rpc("my_reminders", { p_company: companyId })) as Reminder[];

// ---------------------------------------------------------------- Payroll (M8)
export interface PayElement {
  id: string; key: string; name_en: string; name_ar: string | null; kind: "earning" | "deduction";
  taxable: boolean; insurable: boolean; recurring: boolean; active: boolean; sort_order: number;
}
export interface PayItem { id: string; employment_id: string; element_id: string; amount: number; start_date: string; end_date: string | null }
export interface PayrollProfile {
  employment_id: string; insured: boolean; insurance_number: string | null; insurance_salary: number | null;
  payment_method: "bank" | "cash" | "wallet"; bank_name: string | null; bank_account: string | null; tax_exempt: boolean;
}
export type RunStatus = "draft" | "calculated" | "approved" | "paid";
export interface PayrollRun {
  id: string; period: string; pay_date: string | null; status: RunStatus; notes: string | null; is_sample: boolean;
  calculated_at: string | null; approved_at: string | null; paid_at: string | null;
}
export interface PayLine { key: string; name_en: string; name_ar: string | null; kind: "earning" | "deduction"; amount: number; one_off?: boolean }
export interface Payslip {
  id: string; run_id: string; employment_id: string; employee_number: string; full_name: string; full_name_ar: string | null;
  job_title: string | null; department_name: string | null; days_in_month: number; days_paid: number; unpaid_days: number;
  gross: number; insurable_wage: number; si_employee: number; si_employer: number; health_employee: number; health_employer: number;
  taxable_annual: number; income_tax: number; martyrs_fund: number; other_deductions: number; net: number; employer_cost: number;
  lines: PayLine[]; payment_method: string | null; bank_name: string | null; bank_account: string | null; insurance_number: string | null;
}
export interface Adjustment { id: string; employment_id: string; element_id: string; amount: number; note: string | null; created_at: string }

export const loadPayElements = async (companyId: string) =>
  check(await db().from("pay_elements").select("*").eq("company_id", companyId).order("sort_order")) as PayElement[];
export async function savePayElement(companyId: string, el: Partial<PayElement> & { id?: string }) {
  if (el.id) { const { id, ...patch } = el; check(await db().from("pay_elements").update(patch).eq("id", id).select()); }
  else check(await db().from("pay_elements").insert({ ...el, company_id: companyId }).select());
}
export const loadPayItems = async (companyId: string) =>
  check(await db().from("employee_pay_items").select("id, employment_id, element_id, amount, start_date, end_date").eq("company_id", companyId).order("start_date")) as PayItem[];
export const setPayItem = async (employmentId: string, elementId: string, amount: number, start: string) => {
  check(await db().rpc("set_pay_item", { p_employment: employmentId, p_element: elementId, p_amount: amount, p_start: start }));
};
export const loadPayrollProfiles = async (companyId: string) =>
  check(await db().from("payroll_profiles").select("*").eq("company_id", companyId)) as PayrollProfile[];
export async function savePayrollProfile(companyId: string, p: PayrollProfile) {
  check(await db().from("payroll_profiles").upsert({ ...p, company_id: companyId }, { onConflict: "employment_id" }).select());
}
export const loadPayrollRuns = async (companyId: string) =>
  check(await db().from("payroll_runs").select("id, period, pay_date, status, notes, is_sample, calculated_at, approved_at, paid_at").eq("company_id", companyId).order("period", { ascending: false })) as PayrollRun[];
export const loadRunTotals = async (companyId: string) =>
  check(await db().from("payslips").select("run_id, net, gross, employer_cost").eq("company_id", companyId)) as { run_id: string; net: number; gross: number; employer_cost: number }[];
export const createPayrollRun = async (companyId: string, month: string) =>
  check(await db().rpc("create_payroll_run", { p_company: companyId, p_month: month })) as string;
export const calculatePayroll = async (runId: string) => check(await db().rpc("calculate_payroll", { p_run: runId })) as number;
export const approvePayroll = async (runId: string) => check(await db().rpc("approve_payroll", { p_run: runId })) as RunStatus;
export const reopenPayroll = async (runId: string) => { check(await db().rpc("reopen_payroll", { p_run: runId })); };
export const markPayrollPaid = async (runId: string, on: string) => { check(await db().rpc("mark_payroll_paid", { p_run: runId, p_paid_on: on })); };
export const deletePayrollRun = async (runId: string) => { check(await db().rpc("delete_payroll_run", { p_run: runId })); };
export const loadPayslips = async (runId: string) =>
  check(await db().from("payslips").select("*").eq("run_id", runId).order("full_name")) as Payslip[];
export const loadPayslip = async (id: string) => check(await db().from("payslips").select("*").eq("id", id).single()) as Payslip;
export const loadApprovalsCount = async (runId: string) =>
  (check(await db().from("payroll_run_approvals").select("user_id").eq("run_id", runId)) as { user_id: string }[]);
export const loadPayAdjustments = async (runId: string) =>
  check(await db().from("payroll_adjustments").select("id, employment_id, element_id, amount, note, created_at").eq("run_id", runId).order("created_at")) as Adjustment[];
export const addPayAdjustment = async (runId: string, employmentId: string, elementId: string, amount: number, note: string) =>
  check(await db().rpc("add_payroll_adjustment", { p_run: runId, p_employment: employmentId, p_element: elementId, p_amount: amount, p_note: note || null })) as string;
export const removePayAdjustment = async (id: string) => { check(await db().rpc("remove_payroll_adjustment", { p_adjustment: id })); };
export interface MyPayslip { payslip_id: string; period: string; pay_date: string | null; gross: number; net: number; run_status: RunStatus }
export const loadMyPayslips = async (companyId: string) => check(await db().rpc("my_payslips", { p_company: companyId })) as MyPayslip[];

// ---------------------------------------------------------------- Shifts, overtime, loans (M9)
export interface Shift { id: string; name: string; name_ar: string | null; start_time: string; end_time: string; break_minutes: number; active: boolean }
export interface ShiftAssignment { id: string; employment_id: string; shift_id: string; start_date: string; end_date: string | null }
export type OtKind = "day" | "night" | "rest_day" | "holiday";
export interface OvertimeEntry {
  id: string; employment_id: string; work_date: string; kind: OtKind; minutes: number; source: "attendance" | "manual";
  status: "suggested" | "approved" | "rejected"; note: string | null; payroll_run_id: string | null;
}
export const loadShifts = async (companyId: string) =>
  check(await db().from("shifts").select("id, name, name_ar, start_time, end_time, break_minutes, active").eq("company_id", companyId).order("start_time")) as Shift[];
export async function saveShift(companyId: string, s: Partial<Shift> & { id?: string }) {
  if (s.id) { const { id, ...patch } = s; check(await db().from("shifts").update(patch).eq("id", id).select()); }
  else check(await db().from("shifts").insert({ ...s, company_id: companyId }).select());
}
export const loadShiftAssignments = async (companyId: string) =>
  check(await db().from("shift_assignments").select("id, employment_id, shift_id, start_date, end_date").eq("company_id", companyId)) as ShiftAssignment[];
export const assignShift = async (employmentIds: string[], shiftId: string | null, start: string) =>
  check(await db().rpc("assign_shift", { p_employments: employmentIds, p_shift: shiftId, p_start: start })) as number;
export async function loadOvertime(companyId: string, from: string, to: string): Promise<OvertimeEntry[]> {
  return check(await db().from("overtime_entries").select("id, employment_id, work_date, kind, minutes, source, status, note, payroll_run_id")
    .eq("company_id", companyId).gte("work_date", from).lte("work_date", to).order("work_date")) as OvertimeEntry[];
}
export const loadPendingOvertime = async (companyId: string) =>
  check(await db().from("overtime_entries").select("id, employment_id, work_date, kind, minutes, source, status, note, payroll_run_id")
    .eq("company_id", companyId).eq("status", "suggested").order("work_date")) as OvertimeEntry[];
export const suggestOvertime = async (companyId: string, from: string, to: string) =>
  check(await db().rpc("suggest_overtime", { p_company: companyId, p_from: from, p_to: to })) as number;
export const addOvertime = async (employmentId: string, date: string, kind: OtKind, minutes: number, note: string) =>
  check(await db().rpc("add_overtime", { p_employment: employmentId, p_date: date, p_kind: kind, p_minutes: minutes, p_note: note || null })) as string;
export const decideOvertime = async (ids: string[], approve: boolean) =>
  check(await db().rpc("decide_overtime", { p_entries: ids, p_approve: approve })) as number;

export interface LoanRow {
  loan_id: string; employment_id: string; kind: "loan" | "advance"; principal: number; installments: number; first_month: string | null;
  status: "requested" | "active" | "rejected" | "cancelled"; reason: string | null; created_at: string;
  recovered: number; scheduled: number; outstanding: number; next_due: string | null; next_amount: number | null;
}
export const loadLoans = async (companyId: string) => check(await db().rpc("loan_summary", { p_company: companyId })) as LoanRow[];
export const createLoan = async (employmentId: string, kind: "loan" | "advance", amount: number, installments: number, firstMonth: string, note: string) =>
  check(await db().rpc("create_loan", { p_employment: employmentId, p_kind: kind, p_amount: amount, p_installments: installments, p_first_month: firstMonth, p_note: note || null })) as string;
export const requestLoan = async (companyId: string, kind: "loan" | "advance", amount: number, installments: number, reason: string) =>
  check(await db().rpc("request_loan", { p_company: companyId, p_kind: kind, p_amount: amount, p_installments: installments, p_reason: reason || null })) as string;
export const decideLoan = async (id: string, approve: boolean, firstMonth: string | null, installments: number | null, note: string) =>
  check(await db().rpc("decide_loan", { p_loan: id, p_approve: approve, p_first_month: firstMonth, p_installments: installments, p_note: note || null })) as string;
export const closeLoan = async (id: string, note: string) => { check(await db().rpc("close_loan", { p_loan: id, p_note: note || null })); };

// ---------------------------------------------------------------- Rewards and exit (M10)
export type RewardKey = "bonus" | "commission" | "award" | "profit_share";
export interface RewardPlan { id: string; title: string; element_key: RewardKey; pay_month: string; status: "draft" | "approved" | "paid"; notes: string | null; run_id: string | null; created_at: string }
export interface RewardLine { id: string; plan_id: string; employment_id: string; amount: number; note: string | null }
export const loadRewardPlans = async (companyId: string) =>
  check(await db().from("reward_plans").select("id, title, element_key, pay_month, status, notes, run_id, created_at").eq("company_id", companyId).order("pay_month", { ascending: false })) as RewardPlan[];
export const loadRewardLines = async (companyId: string) =>
  check(await db().from("reward_lines").select("id, plan_id, employment_id, amount, note").eq("company_id", companyId)) as RewardLine[];
export const createRewardPlan = async (companyId: string, title: string, key: RewardKey, month: string, notes: string) =>
  (check(await db().from("reward_plans").insert({ company_id: companyId, title, element_key: key, pay_month: month, notes: notes || null }).select("id").single()) as { id: string }).id;
export const deleteRewardPlan = async (id: string) => { check(await db().from("reward_plans").delete().eq("id", id).select()); };
export const fillRewardPlan = async (id: string, fixed: number | null, months: number | null, dept: string | null) =>
  check(await db().rpc("fill_reward_plan", { p_plan: id, p_fixed: fixed, p_months_of_basic: months, p_department: dept })) as number;
export const setRewardLine = async (plan: string, emp: string, amount: number) => { check(await db().rpc("set_reward_line", { p_plan: plan, p_employment: emp, p_amount: amount })); };
export const approveRewardPlan = async (id: string) => check(await db().rpc("approve_reward_plan", { p_plan: id })) as number;

export interface BenefitPlan { id: string; name_en: string; name_ar: string | null; category: string; provider: string | null; employer_cost: number; employee_cost: number; dependent_factor: number; active: boolean }
export interface Enrollment { id: string; plan_id: string; employment_id: string; start_date: string; end_date: string | null; dependents: number }
export const loadBenefitPlans = async (companyId: string) =>
  check(await db().from("benefit_plans").select("id, name_en, name_ar, category, provider, employer_cost, employee_cost, dependent_factor, active").eq("company_id", companyId).order("name_en")) as BenefitPlan[];
export async function saveBenefitPlan(companyId: string, p: Partial<BenefitPlan> & { id?: string }) {
  if (p.id) { const { id, ...patch } = p; check(await db().from("benefit_plans").update(patch).eq("id", id).select()); }
  else check(await db().from("benefit_plans").insert({ ...p, company_id: companyId }).select());
}
export const loadEnrollments = async (companyId: string) =>
  check(await db().from("benefit_enrollments").select("id, plan_id, employment_id, start_date, end_date, dependents").eq("company_id", companyId)) as Enrollment[];
export const enrollBenefit = async (plan: string, emps: string[], start: string, dependents: number) =>
  check(await db().rpc("enroll_benefit", { p_plan: plan, p_employments: emps, p_start: start, p_dependents: dependents })) as number;
export const endBenefit = async (id: string, end: string) => { check(await db().rpc("end_benefit", { p_enrollment: id, p_end: end })); };

export type ExpenseStatus = "submitted" | "manager_approved" | "approved" | "rejected" | "cancelled";
export interface ExpenseClaim {
  id: string; employment_id: string; claim_date: string; category: string; description: string; amount: number; receipt_ref: string | null; receipt_path: string | null;
  status: ExpenseStatus; decision_note: string | null; payroll_run_id: string | null; created_at: string;
}
export async function loadExpenses(companyId: string, employmentId?: string): Promise<ExpenseClaim[]> {
  let q = db().from("expense_claims").select("id, employment_id, claim_date, category, description, amount, receipt_ref, receipt_path, status, decision_note, payroll_run_id, created_at")
    .eq("company_id", companyId).order("claim_date", { ascending: false }).limit(500);
  if (employmentId) q = q.eq("employment_id", employmentId);
  return check(await q) as ExpenseClaim[];
}
export const submitExpense = async (companyId: string, date: string, category: string, description: string, amount: number, receipt: string, employmentId?: string) =>
  check(await db().rpc("submit_expense", { p_company: companyId, p_date: date, p_category: category, p_description: description, p_amount: amount, p_receipt: receipt || null, p_employment: employmentId ?? null })) as string;
export const decideExpense = async (ids: string[], approve: boolean, note?: string) =>
  check(await db().rpc("decide_expense", { p_claims: ids, p_approve: approve, p_note: note || null })) as number;
export const cancelExpense = async (id: string) => { check(await db().rpc("cancel_expense", { p_claim: id })); };

export interface Asset { id: string; tag: string; name: string; category: string; serial: string | null; value: number | null; status: string; notes: string | null }
export interface AssetAssignment { id: string; asset_id: string; employment_id: string; issued_on: string; returned_on: string | null; return_condition: string | null }
export const loadAssets = async (companyId: string) =>
  check(await db().from("assets").select("id, tag, name, category, serial, value, status, notes").eq("company_id", companyId).order("tag")) as Asset[];
export const loadAssetAssignments = async (companyId: string) =>
  check(await db().from("asset_assignments").select("id, asset_id, employment_id, issued_on, returned_on, return_condition").eq("company_id", companyId).order("issued_on", { ascending: false })) as AssetAssignment[];
export async function saveAsset(companyId: string, a: Partial<Asset> & { id?: string }) {
  if (a.id) { const { id, ...patch } = a; check(await db().from("assets").update(patch).eq("id", id).select()); }
  else check(await db().from("assets").insert({ ...a, company_id: companyId }).select());
}
export const issueAsset = async (asset: string, emp: string, on: string, note: string) => { check(await db().rpc("issue_asset", { p_asset: asset, p_employment: emp, p_on: on, p_note: note || null })); };
export const returnAsset = async (asset: string, on: string, condition: string, note: string) => { check(await db().rpc("return_asset", { p_asset: asset, p_on: on, p_condition: condition, p_note: note || null })); };

export interface ExitCase {
  id: string; employment_id: string; exit_type: string; notice_date: string; last_day: string; leaving_reason_code: string | null;
  regrettable: boolean | null; rehire_eligible: boolean | null; status: "open" | "settled" | "closed" | "cancelled";
  leave_days: number; leave_amount: number; notice_months: number; notice_amount: number; gratuity_amount: number; compensation_amount: number;
  loan_amount: number; asset_deduction: number; settlement_run_id: string | null; interview: Record<string, unknown>; notes: string | null; created_at: string;
}
export interface ExitTask { id: string; case_id: string; key: string; title_en: string; title_ar: string | null; owner: string; done_at: string | null; sort_order: number }
export const loadExits = async (companyId: string) =>
  check(await db().from("exit_cases").select("*").eq("company_id", companyId).order("last_day", { ascending: false })) as ExitCase[];
export const loadExitTasks = async (caseId: string) =>
  check(await db().from("exit_tasks").select("id, case_id, key, title_en, title_ar, owner, done_at, sort_order").eq("case_id", caseId).order("sort_order")) as ExitTask[];
export const startExit = async (emp: string, type: string, lastDay: string, reason: string | null, noticeDate: string, notes: string) =>
  check(await db().rpc("start_exit", { p_employment: emp, p_type: type, p_last_day: lastDay, p_reason: reason, p_notice_date: noticeDate, p_notes: notes || null })) as string;
export async function updateExit(id: string, patch: Partial<ExitCase>) { check(await db().from("exit_cases").update(patch).eq("id", id).select()); }
export const calculateExit = async (id: string) => check(await db().rpc("calculate_exit", { p_case: id })) as { monthly_pay: number; daily_wage: number; years_of_service: number; insured: boolean; unreturned_assets: number; dismissal_court_minimum: number };
export const settleExit = async (id: string) => check(await db().rpc("settle_exit", { p_case: id })) as string | null;
export const setExitTask = async (id: string, done: boolean) => { check(await db().rpc("set_exit_task", { p_task: id, p_done: done })); };
export const closeExit = async (id: string, cancel: boolean) => { check(await db().rpc("close_exit", { p_case: id, p_cancel: cancel })); };

// ---------------------------------------------------------------- Recruitment and onboarding (M11a)
export type ReqStatus = "requested" | "open" | "on_hold" | "filled" | "cancelled";
export type Stage = "applied" | "screening" | "interview" | "offer" | "hired" | "rejected" | "withdrawn";
export interface Requisition {
  id: string; title: string; title_ar: string | null; department_id: string | null; headcount: number; contract_type_code: string; reason: string;
  salary_min: number | null; salary_max: number | null; description: string | null; hiring_manager: string | null; status: ReqStatus; created_at: string;
}
export interface Candidate {
  id: string; first_name: string; last_name: string; first_name_ar: string | null; last_name_ar: string | null; email: string | null; mobile: string | null;
  source_code: string | null; cv_link: string | null; cv_path: string | null; current_title: string | null; notes: string | null;
}
export interface Application { id: string; requisition_id: string; candidate_id: string; stage: Stage; rating: number | null; reject_reason: string | null; employment_id: string | null; created_at: string; stage_changed_at: string }
export interface Interview { id: string; application_id: string; scheduled_at: string; kind: string; interviewer: string | null; score: number | null; recommendation: string | null; notes: string | null }
export interface Offer { application_id: string; basic_salary: number; allowances: number; start_date: string; status: string }
export const loadRequisitions = async (companyId: string) =>
  check(await db().from("job_requisitions").select("id, title, title_ar, department_id, headcount, contract_type_code, reason, salary_min, salary_max, description, hiring_manager, status, created_at").eq("company_id", companyId).order("created_at", { ascending: false })) as Requisition[];
export const loadCandidates = async (companyId: string) =>
  check(await db().from("candidates").select("id, first_name, last_name, first_name_ar, last_name_ar, email, mobile, source_code, cv_link, cv_path, current_title, notes").eq("company_id", companyId)) as Candidate[];
export const loadApplications = async (companyId: string) =>
  check(await db().from("applications").select("id, requisition_id, candidate_id, stage, rating, reject_reason, employment_id, created_at, stage_changed_at").eq("company_id", companyId)) as Application[];
export const loadInterviews = async (companyId: string) =>
  check(await db().from("interviews").select("id, application_id, scheduled_at, kind, interviewer, score, recommendation, notes").eq("company_id", companyId).order("scheduled_at")) as Interview[];
export const loadOffers = async (companyId: string) =>
  check(await db().from("offers").select("application_id, basic_salary, allowances, start_date, status").eq("company_id", companyId)) as Offer[];
export const requestHire = async (companyId: string, title: string, dept: string | null, headcount: number, reason: string, description: string) =>
  check(await db().rpc("request_hire", { p_company: companyId, p_title: title, p_department: dept, p_headcount: headcount, p_reason: reason, p_description: description || null })) as string;
export async function updateRequisition(id: string, patch: Partial<Requisition>) { check(await db().from("job_requisitions").update(patch).eq("id", id).select()); }
export const setRequisitionStatus = async (id: string, status: ReqStatus) => { check(await db().rpc("set_requisition_status", { p_requisition: id, p_status: status })); };
export const addApplication = async (req: string, candidate: string | null, p: Record<string, string>) =>
  check(await db().rpc("add_application", { p_requisition: req, p_candidate: candidate, p })) as string;
export const moveApplication = async (id: string, stage: Stage, reason?: string, rating?: number | null) =>
  { check(await db().rpc("move_application", { p_application: id, p_stage: stage, p_reason: reason ?? null, p_rating: rating ?? null })); };
export const addInterview = async (app: string, at: string, kind: string, interviewer: string, score: number | null, rec: string | null, notes: string) =>
  check(await db().rpc("add_interview", { p_application: app, p_at: at, p_kind: kind, p_interviewer: interviewer, p_score: score, p_recommendation: rec, p_notes: notes || null })) as string;
export const saveOffer = async (app: string, basic: number, allowances: number, start: string, status: string) =>
  { check(await db().rpc("save_offer", { p_application: app, p_basic: basic, p_allowances: allowances, p_start: start, p_status: status })); };
export const hireCandidate = async (app: string, start: string | null) => check(await db().rpc("hire_candidate", { p_application: app, p_start: start })) as string;
export const loadRecruitmentStats = async (companyId: string) => check(await db().rpc("recruitment_stats", { p_company: companyId })) as {
  open_roles: number; open_positions: number; requested: number; by_stage: Record<string, number>; avg_days_to_hire: number | null; hires_12m: number; by_source: Record<string, number>;
} | null;

export interface OnboardingTask { id: string; employment_id: string; title_en: string; title_ar: string | null; owner: string; due_date: string; done_at: string | null; sort_order: number }
export interface OnboardingTemplate { id: string; title_en: string; title_ar: string | null; owner: string; due_offset: number; from_probation_end: boolean; active: boolean; sort_order: number }
export async function loadOnboardingTasks(companyId: string, employmentId?: string): Promise<OnboardingTask[]> {
  let q = db().from("onboarding_tasks").select("id, employment_id, title_en, title_ar, owner, due_date, done_at, sort_order").eq("company_id", companyId).order("sort_order");
  if (employmentId) q = q.eq("employment_id", employmentId);
  return check(await q) as OnboardingTask[];
}
export const loadOnboardingTemplates = async (companyId: string) =>
  check(await db().from("onboarding_templates").select("id, title_en, title_ar, owner, due_offset, from_probation_end, active, sort_order").eq("company_id", companyId).order("sort_order")) as OnboardingTemplate[];
export async function saveOnboardingTemplate(companyId: string, t: Partial<OnboardingTemplate> & { id?: string }) {
  if (t.id) { const { id, ...patch } = t; check(await db().from("onboarding_templates").update(patch).eq("id", id).select()); }
  else check(await db().from("onboarding_templates").insert({ ...t, company_id: companyId }).select());
}
export const startOnboarding = async (emp: string) => check(await db().rpc("start_onboarding", { p_employment: emp })) as number;
export const setOnboardingTask = async (id: string, done: boolean) => { check(await db().rpc("set_onboarding_task", { p_task: id, p_done: done })); };
export const loadHireSources = async () => check(await db().from("hire_sources").select("code, name_en, name_ar").order("sort_order")) as { code: string; name_en: string; name_ar: string }[];

// ---------------------------------------------------------------- Performance and training (M11b)
export interface ReviewCycle { id: string; name: string; kind: "annual" | "mid_year" | "quarterly" | "probation"; period_start: string; period_end: string; self_due: string | null; manager_due: string | null; status: "draft" | "active" | "closed" }
export interface Review {
  id: string; cycle_id: string; employment_id: string; reviewer_employment: string | null; status: "self" | "manager" | "completed" | "acknowledged";
  self_rating: number | null; self_comments: string | null; self_submitted_at: string | null; manager_rating: number | null; manager_comments: string | null;
  strengths: string | null; improvements: string | null; probation_outcome: string | null; manager_submitted_at: string | null; acknowledged_at: string | null; employee_comment: string | null;
}
export interface Goal { id: string; employment_id: string; title: string; description: string | null; weight: number; due_date: string | null; progress: number; status: string }
export const loadCycles = async (companyId: string) =>
  check(await db().from("review_cycles").select("id, name, kind, period_start, period_end, self_due, manager_due, status").eq("company_id", companyId).order("period_end", { ascending: false })) as ReviewCycle[];
export async function createCycle(companyId: string, c: Omit<ReviewCycle, "id" | "status">) {
  return (check(await db().from("review_cycles").insert({ ...c, company_id: companyId }).select("id").single()) as { id: string }).id;
}
export const loadReviews = async (companyId: string) =>
  check(await db().from("reviews").select("*").eq("company_id", companyId)) as Review[];
export const launchCycle = async (id: string) => check(await db().rpc("launch_cycle", { p_cycle: id })) as number;
export const closeCycle = async (id: string) => { check(await db().rpc("close_cycle", { p_cycle: id })); };
export const submitSelfReview = async (id: string, rating: number, comments: string) => { check(await db().rpc("submit_self_review", { p_review: id, p_rating: rating, p_comments: comments })); };
export const submitManagerReview = async (id: string, rating: number, comments: string, strengths: string, improvements: string, outcome: string | null) =>
  { check(await db().rpc("submit_manager_review", { p_review: id, p_rating: rating, p_comments: comments, p_strengths: strengths || null, p_improvements: improvements || null, p_probation_outcome: outcome })); };
export const acknowledgeReview = async (id: string, comment: string) => { check(await db().rpc("acknowledge_review", { p_review: id, p_comment: comment || null })); };
export const loadGoals = async (companyId: string) =>
  check(await db().from("goals").select("id, employment_id, title, description, weight, due_date, progress, status").eq("company_id", companyId).order("created_at")) as Goal[];
export const saveGoal = async (id: string | null, emp: string | null, title: string | null, weight: number | null, due: string | null, progress: number | null, status: string | null) =>
  check(await db().rpc("save_goal", { p_goal: id, p_employment: emp, p_title: title, p_description: null, p_weight: weight, p_due: due, p_progress: progress, p_status: status })) as string;
export const deleteGoal = async (id: string) => { check(await db().from("goals").delete().eq("id", id).select()); };

export interface Course { id: string; title: string; title_ar: string | null; provider: string | null; category: string; mode: string; hours: number; cost: number; mandatory: boolean; validity_months: number | null; active: boolean }
export interface TrainingSession { id: string; course_id: string; starts_on: string; ends_on: string; location: string | null; trainer: string | null; capacity: number | null; status: string }
export interface TrainingRecord { id: string; employment_id: string; course_id: string; session_id: string | null; status: string; reason: string | null; completed_on: string | null; score: number | null; certificate_no: string | null; certificate_path: string | null; expires_on: string | null }
export const loadCourses = async (companyId: string) =>
  check(await db().from("courses").select("id, title, title_ar, provider, category, mode, hours, cost, mandatory, validity_months, active").eq("company_id", companyId).order("title")) as Course[];
export async function saveCourse(companyId: string, c: Partial<Course> & { id?: string }) {
  if (c.id) { const { id, ...patch } = c; check(await db().from("courses").update(patch).eq("id", id).select()); }
  else check(await db().from("courses").insert({ ...c, company_id: companyId }).select());
}
export const loadSessions = async (companyId: string) =>
  check(await db().from("training_sessions").select("id, course_id, starts_on, ends_on, location, trainer, capacity, status").eq("company_id", companyId).order("starts_on", { ascending: false })) as TrainingSession[];
export async function saveSession(companyId: string, s: Partial<TrainingSession> & { id?: string }) {
  if (s.id) { const { id, ...patch } = s; check(await db().from("training_sessions").update(patch).eq("id", id).select()); }
  else check(await db().from("training_sessions").insert({ ...s, company_id: companyId }).select());
}
export const loadTrainingRecords = async (companyId: string) =>
  check(await db().from("training_records").select("id, employment_id, course_id, session_id, status, reason, completed_on, score, certificate_no, certificate_path, expires_on").eq("company_id", companyId)) as TrainingRecord[];
export const enrollTraining = async (course: string, session: string | null, emps: string[]) =>
  check(await db().rpc("enroll_training", { p_course: course, p_session: session, p_employments: emps })) as number;
export const completeTraining = async (ids: string[], on: string, status: string, score: number | null) =>
  check(await db().rpc("complete_training", { p_records: ids, p_completed: on, p_status: status, p_score: score })) as number;
export const requestTraining = async (course: string, reason: string) => check(await db().rpc("request_training", { p_course: course, p_reason: reason || null })) as string;
export const deleteTrainingRecord = async (id: string) => { check(await db().from("training_records").delete().eq("id", id).select()); };

// ---------------------------------------------------------------- Relations, engagement, analytics (M11c)
export interface ErCase {
  id: string; employment_id: string; kind: "disciplinary" | "grievance"; category: string; title: string; description: string | null; incident_date: string | null;
  status: "open" | "investigating" | "decided" | "closed"; confidential: boolean; hearing_held: boolean; outcome: string | null; penalty_days: number | null;
  penalty_amount: number | null; decision_note: string | null; decided_at: string | null; created_at: string; raised_by: string | null;
}
export interface ErNote { id: string; case_id: string; kind: string; body: string; created_at: string }
export const loadErCases = async (companyId: string) =>
  check(await db().from("er_cases").select("id, employment_id, kind, category, title, description, incident_date, status, confidential, hearing_held, outcome, penalty_days, penalty_amount, decision_note, decided_at, created_at, raised_by").eq("company_id", companyId).order("created_at", { ascending: false })) as ErCase[];
export const loadErNotes = async (caseId: string) =>
  check(await db().from("er_notes").select("id, case_id, kind, body, created_at").eq("case_id", caseId).order("created_at")) as ErNote[];
export const openErCase = async (emp: string, kind: string, category: string, title: string, description: string, incident: string, confidential: boolean) =>
  check(await db().rpc("open_er_case", { p_employment: emp, p_kind: kind, p_category: category, p_title: title, p_description: description || null, p_incident: incident, p_confidential: confidential })) as string;
export const raiseGrievance = async (companyId: string, category: string, title: string, description: string) =>
  check(await db().rpc("raise_grievance", { p_company: companyId, p_category: category, p_title: title, p_description: description })) as string;
export const addErNote = async (id: string, kind: string, body: string) => check(await db().rpc("add_er_note", { p_case: id, p_kind: kind, p_body: body })) as string;
export const decideErCase = async (id: string, outcome: string, days: number | null, note: string) =>
  { check(await db().rpc("decide_er_case", { p_case: id, p_outcome: outcome, p_days: days, p_note: note || null })); };
export const closeErCase = async (id: string) => { check(await db().rpc("close_er_case", { p_case: id })); };

export interface Announcement { id: string; title: string; body: string; pinned: boolean; published_at: string; expires_on: string | null }
export const loadAnnouncements = async (companyId: string) =>
  check(await db().from("announcements").select("id, title, body, pinned, published_at, expires_on").eq("company_id", companyId).order("pinned", { ascending: false }).order("published_at", { ascending: false }).limit(50)) as Announcement[];
export const loadMyReads = async () => (check(await db().from("announcement_reads").select("announcement_id")) as { announcement_id: string }[]).map((r) => r.announcement_id);
export async function markRead(id: string) {
  const uid = (await db().auth.getSession()).data.session?.user.id;
  if (uid) await db().from("announcement_reads").upsert({ announcement_id: id, user_id: uid }, { onConflict: "announcement_id,user_id", ignoreDuplicates: true });
}
export async function saveAnnouncement(companyId: string, a: Partial<Announcement> & { id?: string }) {
  if (a.id) { const { id, ...patch } = a; check(await db().from("announcements").update(patch).eq("id", id).select()); }
  else check(await db().from("announcements").insert({ ...a, company_id: companyId }).select());
}
export const deleteAnnouncement = async (id: string) => { check(await db().from("announcements").delete().eq("id", id).select()); };
export interface SurveyQ { id: string; kind: "scale" | "enps" | "text" | "choice"; text: string; options?: string[] }
export interface Survey { id: string; title: string; intro: string | null; anonymous: boolean; status: "draft" | "open" | "closed"; closes_on: string | null; questions: SurveyQ[]; created_at: string }
export const loadSurveys = async (companyId: string) =>
  check(await db().from("surveys").select("id, title, intro, anonymous, status, closes_on, questions, created_at").eq("company_id", companyId).order("created_at", { ascending: false })) as Survey[];
export async function saveSurvey(companyId: string, s: Partial<Survey> & { id?: string }) {
  if (s.id) { const { id, ...patch } = s; check(await db().from("surveys").update(patch).eq("id", id).select()); }
  else check(await db().from("surveys").insert({ ...s, company_id: companyId }).select());
}
export const loadMySurveys = async (companyId: string) => check(await db().rpc("my_surveys", { p_company: companyId })) as (Survey & { answered: boolean })[];
export const submitSurvey = async (id: string, answers: Record<string, unknown>) => { check(await db().rpc("submit_survey", { p_survey: id, p_answers: answers })); };
export interface SurveyResult { responses: number; invited: number; hidden: boolean; questions: { id: string; kind: string; text: string; answers?: number; average?: number; spread?: Record<string, number>; enps?: number | null; by_department?: Record<string, number>; counts?: Record<string, number>; comments?: string[] }[] }
export const loadSurveyResults = async (id: string) => check(await db().rpc("survey_results", { p_survey: id })) as SurveyResult;
export interface Kudos { id: string; from_employment: string; to_employment: string; value: string; message: string; created_at: string }
export const loadKudos = async (companyId: string) =>
  check(await db().from("kudos").select("id, from_employment, to_employment, value, message, created_at").eq("company_id", companyId).order("created_at", { ascending: false }).limit(60)) as Kudos[];
export const giveKudos = async (companyId: string, to: string, value: string, message: string) =>
  check(await db().rpc("give_kudos", { p_company: companyId, p_to: to, p_value: value, p_message: message })) as string;
export interface Colleague { employment_id: string; full_name: string; full_name_ar: string | null; department_name: string | null; department_name_ar: string | null; job_title: string | null }
export const loadColleagues = async (companyId: string) => check(await db().rpc("colleagues", { p_company: companyId })) as Colleague[];
export const loadPeopleAnalytics = async (companyId: string, from: string, to: string) =>
  check(await db().rpc("people_analytics", { p_company: companyId, p_from: from, p_to: to })) as Record<string, Record<string, unknown> | number | string>;

// ---------------------------------------------------------------- Files (private Supabase Storage bucket "files")
export type FileArea = "documents" | "receipts" | "certificates" | "cv";
export const MAX_FILE_MB = 10;
export const FILE_ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,.xls,.xlsx";
/** The file name without the random prefix added on upload. */
export const fileLabel = (path: string) => path.split("/").pop()!.replace(/^[0-9a-z]{8}-/, "");
/** Uploads to {company}/{area}/{owner}/{random}-{name}, links it to the record and removes the file it replaces. */
export async function uploadFile(companyId: string, area: FileArea, ownerId: string, recordId: string, file: File): Promise<string> {
  if (file.size > MAX_FILE_MB * 1024 * 1024) throw new Error(`Files must be ${MAX_FILE_MB} MB or smaller.`);
  const safe = file.name.normalize("NFKD").replace(/[^\w.\-]+/g, "_").replace(/_+/g, "_").slice(-80) || "file";
  const path = `${companyId}/${area}/${ownerId}/${crypto.randomUUID().slice(0, 8)}-${safe}`;
  const up = await db().storage.from("files").upload(path, file, { contentType: file.type || undefined, upsert: false });
  if (up.error) throw new Error(up.error.message);
  try {
    const old = check(await db().rpc("attach_file", { p_area: area, p_id: recordId, p_path: path })) as string | null;
    if (old) await db().storage.from("files").remove([old]);
  } catch (e) {
    await db().storage.from("files").remove([path]);
    throw e;
  }
  return path;
}
export async function removeFile(area: FileArea, recordId: string) {
  const old = check(await db().rpc("attach_file", { p_area: area, p_id: recordId, p_path: null })) as string | null;
  if (old) await db().storage.from("files").remove([old]);
}
/** Opens the file in a new tab with a short-lived private link. */
export async function openFile(path: string) {
  const tab = window.open("", "_blank");
  const r = await db().storage.from("files").createSignedUrl(path, 120);
  if (r.error || !r.data) { tab?.close(); throw new Error(r.error?.message ?? "File not found"); }
  if (tab) tab.location.href = r.data.signedUrl; else window.location.href = r.data.signedUrl;
}
export const loadDocFiles = async (companyId: string) =>
  check(await db().from("employee_documents").select("id, employment_id, document_type_id, file_path").eq("company_id", companyId)) as { id: string; employment_id: string; document_type_id: string; file_path: string | null }[];
